import React from "react";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { UploadOutlined, DeleteOutlined } from "us.icons";
import { UploadProps } from "us.collection.input-output/components/FileConverter/interface";

const { $Upload, $Typography, $Popconfirm, $Message } = Common.Components;

const { Dragger } = $Upload;
const { Text } = $Typography;

/**
 * @description - FileConverter uploader component for select files.
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @link Task - https://unicorn-solutions.atlassian.net/browse/UBX4-25356
 * @since 17/06/2025
 */
const Uploader: React.FC<UploadProps> = (props) => {
  const { t } = useTranslation();

  const { fileList, onChangeFile, uploadRules, disabled = false } = props;
  const { maxSize, maxCount, formats } = uploadRules ?? {};

  const draggerProps = {
    beforeUpload: (file: any, list: any) => {
      const newList = list;

      if (file.size / 1024 / 1024 > maxSize) {
        $Message.error(t("US.COMMON:COMMON.FILE_SIZE_EXCEEDED"));
      } else {
        newList.forEach((item: any) => {
          const isListMax = item.size / 1024 / 1024 > maxSize;
          if (isListMax) {
            item.status = "error";
            item.response = t("US.COMMON:COMMON.FILE_SIZE_EXCEEDED");
          }
        });
        onChangeFile(newList);
      }
      return false;
    },
    accept: formats,
    multiple: false,
    fileList,
    showUploadList: {
      showRemoveIcon: true,
      removeIcon: (file: any) => (
        <$Popconfirm
          title={t("US.COMMON:COMMON.SURE_TO_DELETE_?")}
          data-testid="remove-pop-confirm"
          okText={t("US.COMMON:COMMON.YES")}
          cancelText={t("US.COMMON:COMMON.NO")}
          onConfirm={() => {
            const index = fileList.indexOf(file);
            fileList?.splice(index, 1);
            onChangeFile([...fileList]);
          }}
          placement="right"
        >
          <DeleteOutlined data-testid="remove-icon" />
        </$Popconfirm>
      ),
    },
    maxCount: maxCount,
    disabled: disabled,
  };

  return (
    <>
      <div
        className={
          fileList.length >= maxCount ? "mt-4 mb-5 upload-disable" : "mt-4 mb-5"
        }
      >
        <Dragger
          {...draggerProps}
          name="fileUpload"
          data-testid="upload-dragger"
          onChange={(file: any) => {
            const formData = new FormData();
            formData.append("files[]", file);
          }}
        >
          <p className="ant-upload-drag-icon mb-0 mr-3">
            <UploadOutlined />
          </p>
          <div>
            <p className="ant-upload-text">
              {t("US.COMMON:COMMON.CLICK_OR_DRAG_FILE_TO_THIS_AREA_TO_UPLOAD")}
            </p>
            <p className="ant-upload-hint">
              {t("US.COMMON:COMMON.SUPPORT_FOR_A_SINGLE_FILE_UPLOAD")}
            </p>
            <div>
              <Text type="secondary">
                {t("US.COMMON:COMMON.ONLY_ALLOWED") +
                  ` ${formats} ` +
                  `(${t("US.COMMON:COMMON.MAXIMUM_UPLOAD_SIZE")} ${maxSize}MB)`}
              </Text>
            </div>
          </div>
        </Dragger>
      </div>
    </>
  );
};

export default Uploader;
